import { MenuItem, SelectItem, SelectItemGroup } from 'primeng/api';

import { statesClear2 } from '../../cmg-1003/models/lookups/common.lookups';
import {
  AddScenarioPricingTabFrom,
  MiQuote,
  MiQuoteViewModel,
  PaymentTabs,
  PmiType,
  RunBestXMiResponse,
  RunBestXMiResult,
  ScenarioCriteriaModel,
  SearchResultInformation,
  Suggestion,
  TableTab,
} from './scenario-pricing.models';
import { FormControl, FormGroup } from '@angular/forms';
import { PricingInputFormModel } from './pricing-input-form.model';

export interface SmartSelectItem<T> extends SelectItem<T> {
  selectType: 'radio' | 'checkbox' | 'link';
}

function generateLabelMap<T extends number>(options: SelectItem<T>[]): Record<T, string> {
  return options.reduce((record: Partial<Record<T, string>>, option) => {
    record[option.value] = option.label;
    return record;
  }, {}) as Record<T, string>;
}

export interface SmartSelectItemGroup<T> extends SelectItemGroup {
  items: SmartSelectItem<T>[];
}

export const yesNoOptions: SelectItem<boolean>[] = [
  { label: 'No', value: false },
  { label: 'Yes', value: true },
];

export enum LoanPurpose {
  Purchase = 1,
  RefiCashout = 2,
  RefiRateTermLimitedCO = 3,
  FHAStreamlineRefi = 4,
  VARateReduction = 5,
  SimpleRefinance = 6,
}

export const loanPurposeOptions: SelectItem<LoanPurpose>[] = [
  { label: 'Purchase', value: LoanPurpose.Purchase },
  { label: 'Refi Cashout', value: LoanPurpose.RefiCashout },
  { label: 'Refi Rate-Term/Limited C.O.', value: LoanPurpose.RefiRateTermLimitedCO },
  { label: 'FHA Streamline Refi', value: LoanPurpose.FHAStreamlineRefi },
  { label: 'VA Rate Reduction', value: LoanPurpose.VARateReduction },
];
export const loanPurposeLabel = generateLabelMap(loanPurposeOptions);

// loanPurposeSimpleLabel is used ONLY for the scenario name on the pricing-results page.
export const loanPurposeSimpleLabel: Record<LoanPurpose, string> = {
  [LoanPurpose.Purchase]: 'Purchase',
  [LoanPurpose.RefiCashout]: 'Refinance',
  [LoanPurpose.RefiRateTermLimitedCO]: 'Refinance',
  [LoanPurpose.FHAStreamlineRefi]: 'Refinance',
  [LoanPurpose.VARateReduction]: 'Refinance',
  [LoanPurpose.SimpleRefinance]: 'Refinance',
};

export enum MortgageType {
  None = 0,
  Conforming = 1,
  NonConforming = 2,
  FHA = 4,
  VA = 8,
  USDA = 16,
}

export const mortgageTypeOptions: SmartSelectItemGroup<MortgageType>[] = [
  {
    label: 'Conventional',
    items: [
      {
        label: 'Conforming',
        value: MortgageType.Conforming,
        selectType: 'checkbox',
      },
      {
        label: 'Non Conforming',
        value: MortgageType.NonConforming,
        selectType: 'checkbox',
      },
    ],
  },
  {
    label: 'Government',
    items: [
      {
        label: 'FHA',
        value: MortgageType.FHA,
        selectType: 'radio',
      },
      {
        label: 'VA',
        value: MortgageType.VA,
        selectType: 'radio',
      },
      {
        label: 'USDA',
        value: MortgageType.USDA,
        selectType: 'radio',
      },
      {
        label: 'None',
        value: MortgageType.None,
        selectType: 'radio',
      },
    ],
  },
];

export const mortgageTypeOptionsFlat: SelectItem<MortgageType>[] = [
  { label: 'None', value: MortgageType.None },
  { label: 'Conforming', value: MortgageType.Conforming },
  { label: 'Non Conforming', value: MortgageType.NonConforming },
  { label: 'FHA', value: MortgageType.FHA },
  { label: 'VA', value: MortgageType.VA },
  { label: 'USDA', value: MortgageType.USDA },
];

/**
 * On Scenarios tab, we do not want to include the None enum option
 */
export const mortgageTypeOptionsScenarios: SelectItem<MortgageType>[] = [
  { label: 'Conforming', value: MortgageType.Conforming },
  { label: 'Non Conforming', value: MortgageType.NonConforming },
  { label: 'FHA', value: MortgageType.FHA },
  { label: 'VA', value: MortgageType.VA },
  { label: 'USDA', value: MortgageType.USDA },
];

export const mortgageTypeLabel = generateLabelMap(mortgageTypeOptionsFlat);

export const conventionalMortgageTypes = new Set<MortgageType>([MortgageType.Conforming, MortgageType.NonConforming]);

export const govtMortgageTypes = new Set<MortgageType>([MortgageType.VA, MortgageType.FHA, MortgageType.USDA]);

export enum LoanTerm {
  Year40 = 1,
  Year30 = 2,
  Year25 = 4,
  Year20 = 8,
  Year15 = 16,
  Year10 = 32,
}

export const loanTermKeyMap: Record<LoanTerm, string> = {
  [LoanTerm.Year40]: 'Year40',
  [LoanTerm.Year30]: 'Year30',
  [LoanTerm.Year25]: 'Year25',
  [LoanTerm.Year20]: 'Year20',
  [LoanTerm.Year15]: 'Year15',
  [LoanTerm.Year10]: 'Year10',
};

export const loanTermOptions: SelectItem<LoanTerm>[] = [
  { label: '40 Yr', value: LoanTerm.Year40 },
  { label: '30 Yr', value: LoanTerm.Year30 },
  { label: '25 Yr', value: LoanTerm.Year25 },
  { label: '20 Yr', value: LoanTerm.Year20 },
  { label: '15 Yr', value: LoanTerm.Year15 },
  { label: '10 Yr', value: LoanTerm.Year10 },
];
export const loanTermLabel = generateLabelMap(loanTermOptions);

export const loanTermMosLabel: Record<LoanTerm, string> = {
  [LoanTerm.Year10]: '120',
  [LoanTerm.Year15]: '180',
  [LoanTerm.Year20]: '240',
  [LoanTerm.Year25]: '300',
  [LoanTerm.Year30]: '360',
  [LoanTerm.Year40]: '480',
};

export const loanTermMosToYearLabel: Record<string, LoanTerm> = {
  [120]: LoanTerm.Year10,
  [180]: LoanTerm.Year15,
  [240]: LoanTerm.Year20,
  [300]: LoanTerm.Year25,
  [360]: LoanTerm.Year30,
  [480]: LoanTerm.Year40,
};

export const loanTermLongLabel: Record<LoanTerm, string> = {
  [LoanTerm.Year10]: '10 Year',
  [LoanTerm.Year15]: '15 Year',
  [LoanTerm.Year20]: '20 Year',
  [LoanTerm.Year25]: '25 Year',
  [LoanTerm.Year30]: '30 Year',
  [LoanTerm.Year40]: '40 Year',
};

export const loanTermShortLabel: Record<LoanTerm, string> = {
  [LoanTerm.Year10]: '10 Yr',
  [LoanTerm.Year15]: '15 Yr',
  [LoanTerm.Year20]: '20 Yr',
  [LoanTerm.Year25]: '25 Yr',
  [LoanTerm.Year30]: '30 Yr',
  [LoanTerm.Year40]: '40 Yr',
};

export enum ArmFixedTerm {
  Year3 = 1,
  Year5 = 2,
  Year7 = 4,
  Year10 = 8,
}

export const armFixedTermShortLabel = {
  [ArmFixedTerm.Year3]: '3',
  [ArmFixedTerm.Year5]: '5',
  [ArmFixedTerm.Year7]: '7',
  [ArmFixedTerm.Year10]: '10',
};

export enum ArmSubsequentChangePeriod {
  OneYear = 1,
  SixMonth = 2,
}

export const armSubsequentChangePeriodShortLabel = {
  [ArmSubsequentChangePeriod.OneYear]: '1',
  [ArmSubsequentChangePeriod.SixMonth]: '6',
};

export const armSubsequentChangePeriodDisplayLabel = {
  [ArmSubsequentChangePeriod.OneYear]: '1 Yr',
  [ArmSubsequentChangePeriod.SixMonth]: '6 Months',
};

export const armFixedTermOptions: SelectItem<ArmFixedTerm>[] = [
  { label: '3 Yr', value: ArmFixedTerm.Year3 },
  { label: '5 Yr', value: ArmFixedTerm.Year5 },
  { label: '7 Yr', value: ArmFixedTerm.Year7 },
  { label: '10 Yr', value: ArmFixedTerm.Year10 },
];
export const armFixedTermLabel = generateLabelMap(armFixedTermOptions);

export const armSubsequentChangePeriodMap: Record<ArmSubsequentChangePeriod, string> = {
  [ArmSubsequentChangePeriod.OneYear]: '1 Yr',
  [ArmSubsequentChangePeriod.SixMonth]: '6 Months',
};

export const armSubsequentChangePeriodOptions: SelectItem<ArmSubsequentChangePeriod>[] = [
  { label: '1 Yr', value: ArmSubsequentChangePeriod.OneYear },
  { label: '6 Months', value: ArmSubsequentChangePeriod.SixMonth },
];

export enum DesiredLockPeriod {
  Days21 = 21,
  Days30 = 30,
  Days45 = 45,
  Days60 = 60,
  Days90 = 90,
  Days120 = 120,
  Days150 = 150,
  Days180 = 180,
  Days210 = 210,
  Days240 = 240,
  Days270 = 270,
  Days300 = 300,
  Days330 = 330,
  Days360 = 360,
}

export const EXTENDED_LOCK_THRESHOLD = DesiredLockPeriod.Days120;

export const desiredLockPeriodOptions: SelectItem<DesiredLockPeriod>[] = [
  { label: '21 Days', value: DesiredLockPeriod.Days21 },
  { label: '30 Days', value: DesiredLockPeriod.Days30 },
  { label: '45 Days', value: DesiredLockPeriod.Days45 },
  { label: '60 Days', value: DesiredLockPeriod.Days60 },
  { label: '90 Days', value: DesiredLockPeriod.Days90 },
  { label: '120 Days', value: DesiredLockPeriod.Days120 },
  { label: '150 Days', value: DesiredLockPeriod.Days150 },
  { label: '180 Days', value: DesiredLockPeriod.Days180 },
  { label: '210 Days', value: DesiredLockPeriod.Days210 },
  { label: '240 Days', value: DesiredLockPeriod.Days240 },
  { label: '270 Days', value: DesiredLockPeriod.Days270 },
  { label: '300 Days', value: DesiredLockPeriod.Days300 },
  { label: '330 Days', value: DesiredLockPeriod.Days330 },
  { label: '360 Days', value: DesiredLockPeriod.Days360 },
];

export const desiredLockPeriodLabel = generateLabelMap(desiredLockPeriodOptions);

export const stateOptions: SelectItem<string>[] = statesClear2.map((v) => ({ label: v.value, value: v.value }));

export enum OccupancyType {
  PrimaryResidence = 1,
  SecondaryResidence = 2,
  InvestmentProperty = 3,
}

export const occupancyTypeOptions: SelectItem<OccupancyType>[] = [
  { label: 'Primary Residence', value: OccupancyType.PrimaryResidence },
  { label: 'Secondary Residence', value: OccupancyType.SecondaryResidence },
  { label: 'Investment Property', value: OccupancyType.InvestmentProperty },
];
export const occupancyTypeLabel = generateLabelMap(occupancyTypeOptions);

export enum PropertyType {
  SingleFamily = 1,
  Condo = 2,
  PUD = 3,
  ManufacturedSingle = 4,
  ManufacturedDouble = 5,
  Coop = 6,
  DetachedCondo = 7,
  Condotel = 8,
  NonWarrantableCondo = 9,
  Townhouse = 10,
}

export const propertyTypeOptions: SelectItem<PropertyType>[] = [
  { label: 'Condo', value: PropertyType.Condo },
  { label: 'Condotel', value: PropertyType.Condotel },
  { label: 'Coop', value: PropertyType.Coop },
  { label: 'Detached Condo', value: PropertyType.DetachedCondo },
  { label: 'Manufactured/Double-Wide', value: PropertyType.ManufacturedDouble },
  { label: 'Manufactured/Single-Wide', value: PropertyType.ManufacturedSingle },
  { label: 'Non-Warrantable Condo', value: PropertyType.NonWarrantableCondo },
  { label: 'Single Family', value: PropertyType.SingleFamily },
  { label: 'Townhouse', value: PropertyType.Townhouse },
];
export const propertyTypeLabel = generateLabelMap(propertyTypeOptions);

export enum NumberOfUnits {
  OneUnit = 1,
  TwoUnit = 2,
  ThreeUnits = 3,
  FourUnits = 4,
}

export const noOfUnitsOptions: SelectItem<NumberOfUnits>[] = [
  { label: '1 Unit', value: NumberOfUnits.OneUnit },
  { label: '2 Units', value: NumberOfUnits.TwoUnit },
  { label: '3 Units', value: NumberOfUnits.ThreeUnits },
  { label: '4 Units', value: NumberOfUnits.FourUnits },
];
export const noOfUnitsLabel = generateLabelMap(noOfUnitsOptions);

export enum DesiredType {
  Price = 'Price',
  Rate = 'Rate',
}

export const desiredTypeOptions: SelectItem<DesiredType>[] = [
  { label: 'Price', value: DesiredType.Price },
  { label: 'Rate', value: DesiredType.Rate },
];

export enum BuyDown {
  None = 0,
  B321 = 1,
  B21 = 2,
  B11 = 3,
  B10 = 4,
}

export const buyDownOptions: SelectItem<BuyDown>[] = [
  { label: 'None', value: BuyDown.None },
  { label: '3/2/1', value: BuyDown.B321 },
  { label: '2/1', value: BuyDown.B21 },
  { label: '1/1', value: BuyDown.B11 },
  { label: '1/0', value: BuyDown.B10 },
];
export const buyDownLabel = generateLabelMap(buyDownOptions);

export enum SecondMortgage {
  None = 0,
  Fixed2nd = 1,
  HELOC = 2,
}

export const secondMortgageOptions: SelectItem<SecondMortgage>[] = [
  { label: 'None', value: SecondMortgage.None },
  { label: 'Fixed 2nd', value: SecondMortgage.Fixed2nd },
  { label: 'HELOC', value: SecondMortgage.HELOC },
];
export const secondMortgageLabel = generateLabelMap(secondMortgageOptions);

export enum AmortizationType {
  Fixed = 1,
  ARM = 2,
}

export const amortizationTypeOptions: SelectItem<AmortizationType>[] = [
  { label: 'Fixed', value: AmortizationType.Fixed },
  { label: 'ARM', value: AmortizationType.ARM },
];
export const amortizationTypeLabel = generateLabelMap(amortizationTypeOptions);

export enum Citizenship {
  UsCitizen = 1,
  PermResidentAlien = 2,
  NonPermResidentAlien = 3,
  ForeignNational = 4,
}

export const citizenshipOptions: SelectItem<Citizenship>[] = [
  { label: 'US Citizen', value: Citizenship.UsCitizen },
  { label: 'Perm Resident Alien', value: Citizenship.PermResidentAlien },
  { label: 'Non Perm Resident Alien', value: Citizenship.NonPermResidentAlien },
  { label: 'Foreign National', value: Citizenship.ForeignNational },
];
export const citizenshipLabel = generateLabelMap(citizenshipOptions);

export enum AutomatedUWSystem {
  NotSpecified = 0,
  DU = 1,
  LP = 2,
  InvestorAus = 3,
  ManualTraditional = 4,
}

export const automatedUWSystemOptions: SelectItem<AutomatedUWSystem>[] = [
  { label: 'Not Specified', value: AutomatedUWSystem.NotSpecified },
  { label: 'DU', value: AutomatedUWSystem.DU },
  { label: 'LP', value: AutomatedUWSystem.LP },
  { label: 'Investor AUS', value: AutomatedUWSystem.InvestorAus },
  { label: 'Manual/Traditional', value: AutomatedUWSystem.ManualTraditional },
];
export const automatedUWSystemLabel = generateLabelMap(automatedUWSystemOptions);

export enum VaFundingFeeType {
  RegularMilitaryFirstUse = 1,
  RegularMilitarySubsequentUse = 2,
  ReservesOrGuardFirstUse = 3,
  ReservesOrGuardSubsequentUse = 4,
}

export const vaFundingFeeTypeOptions: SelectItem<VaFundingFeeType>[] = [
  { label: 'Regular Military First Use', value: VaFundingFeeType.RegularMilitaryFirstUse },
  { label: 'Regular Military Subsequent Use', value: VaFundingFeeType.RegularMilitarySubsequentUse },
  { label: 'Reserves Or Guard First Use', value: VaFundingFeeType.ReservesOrGuardFirstUse },
  { label: 'Reserves Or Guard Subsequent Use', value: VaFundingFeeType.ReservesOrGuardSubsequentUse },
];
export const vaFundingFeeTypeLabel = generateLabelMap(vaFundingFeeTypeOptions);

export enum FhaCaseAssigned {
  Before07142008 = 1,
  Between07142008And09302008 = 2,
  Between10012008And12312008 = 3,
  Between01012009And04042010 = 4,
  Between04052010And10032010 = 5,
  Between10042010And04172011 = 6,
  Between04182011And04082012 = 7,
  Between04092012And06102012 = 8,
  Between06112012And03312013 = 9,
  Between04012013And06022013 = 10,
  Between06032013And01252015 = 11,
  Between01262015And09302016 = 12,
  Between10012016And01262017 = 13,
  Between01272017And08312019 = 14,
  Between09012019And03192023 = 15,
  OnOrAfter03202023 = 16,
}

export const fhaCaseAssignedOptions: SelectItem<FhaCaseAssigned>[] = [
  { label: 'On or After 03/20/2023', value: FhaCaseAssigned.OnOrAfter03202023 },
  { label: '09/01/2019 - 03/19/2023', value: FhaCaseAssigned.Between09012019And03192023 },
  { label: '01/27/2017 - 08/31/2019', value: FhaCaseAssigned.Between01272017And08312019 },
  { label: '10/01/2016 - 01/26/2017', value: FhaCaseAssigned.Between10012016And01262017 },
  { label: '01/26/2015 - 09/30/2016', value: FhaCaseAssigned.Between01262015And09302016 },
  { label: '06/03/2013 - 01/25/2015', value: FhaCaseAssigned.Between06032013And01252015 },
  { label: '04/01/2013 - 06/02/2013', value: FhaCaseAssigned.Between04012013And06022013 },
  { label: '06/11/2012 - 03/31/2013', value: FhaCaseAssigned.Between06112012And03312013 },
  { label: '04/09/2012 - 06/10/2012', value: FhaCaseAssigned.Between04092012And06102012 },
  { label: '04/18/2011 - 04/08/2012', value: FhaCaseAssigned.Between04182011And04082012 },
  { label: '10/04/2010 - 04/17/2011', value: FhaCaseAssigned.Between10042010And04172011 },
  { label: '04/05/2010 - 10/03/2010', value: FhaCaseAssigned.Between04052010And10032010 },
  { label: '01/01/2009 - 04/04/2010', value: FhaCaseAssigned.Between01012009And04042010 },
  { label: '10/01/2008 - 12/31/2008', value: FhaCaseAssigned.Between10012008And12312008 },
  { label: '07/14/2008 - 09/30/2008', value: FhaCaseAssigned.Between07142008And09302008 },
  { label: 'Before 07/14/2008', value: FhaCaseAssigned.Before07142008 },
];

export const fhaCaseAssignedLabel = generateLabelMap(fhaCaseAssignedOptions);

export const fhaCaseAssignedMap = {
  OnOrAfter03202023: 'On or After 03/20/2023',
  Between09012019And03192023: '09/01/2019 - 03/19/2023',
  Between01272017And08312019: '01/27/2017 - 08/31/2019',
  Between10012016And01262017: '10/01/2016 - 01/26/2017',
  Between01262015And09302016: '01/26/2015 - 09/30/2016',
  Between06032013And01252015: '06/03/2013 - 01/25/2015',
  Between04012013And06022013: '04/01/2013 - 06/02/2013',
  Between06112012And03312013: '06/11/2012 - 03/31/2013',
  Between04092012And06102012: '04/09/2012 - 06/10/2012',
  Between04182011And04082012: '04/18/2011 - 04/08/2012',
  Between10042010And04172011: '10/04/2010 - 04/17/2011',
  Between04052010And10032010: '04/05/2010 - 10/03/2010',
  Between01012009And04042010: '01/01/2009 - 04/04/2010',
  Between10012008And12312008: '10/01/2008 - 12/31/2008',
  Between07142008And09302008: '07/14/2008 - 09/30/2008',
  Before07142008: 'Before 07/14/2008',
};

export enum UsdaCaseAssigned {
  OnOrBefore09302011 = 1,
  Between10012011And12062011 = 2,
  Between12072011And09302012 = 3,
  Between10012012And09302014 = 4,
  Between10012014And09302015 = 5,
  Between10012015And09302016 = 6,
  OnOrAfter10012016 = 7,
}

export const usdaCaseAssignedOptions: SelectItem<UsdaCaseAssigned>[] = [
  { label: 'On or After 10/01/2016', value: UsdaCaseAssigned.OnOrAfter10012016 },
  { label: '10/01/2015 - 09/30/2016', value: UsdaCaseAssigned.Between10012015And09302016 },
  { label: '10/01/2014 - 09/30/2015', value: UsdaCaseAssigned.Between10012014And09302015 },
  { label: '10/01/2012 - 09/30/2014', value: UsdaCaseAssigned.Between10012012And09302014 },
  { label: '12/07/2011 - 09/30/2012', value: UsdaCaseAssigned.Between12072011And09302012 },
  { label: '10/01/2011 - 12/06/2011', value: UsdaCaseAssigned.Between10012011And12062011 },
  { label: 'On or Before 09/30/2011', value: UsdaCaseAssigned.OnOrBefore09302011 },
];

export const usdaCaseAssignedLabel = generateLabelMap(usdaCaseAssignedOptions);

export const usdaCaseAssignedMap = {
  OnOrAfter10012016: 'On or After 10/01/2016',
  Between10012015And09302016: '10/01/2015 - 09/30/2016',
  Between10012014And09302015: '10/01/2014 - 09/30/2015',
  Between10012012And09302014: '10/01/2012 - 09/30/2014',
  Between12072011And09302012: '12/07/2011 - 09/30/2012',
  Between10012011And12062011: '10/01/2011 - 12/06/2011',
  OnOrBefore09302011: 'On or Before 09/30/2011',
};

export enum IncomeVerificationType {
  FullDoc = 1,
  AssetRelated = 2,
  OneYear1099 = 3,
  TwoYear1099 = 4,
  BusinessBankStmt12Mos = 5,
  BusinessBankStmt24Mos = 6,
  PersonalBankStmt12Mos = 7,
  PersonalBankStmt24Mos = 8,
  PLOneYear = 9,
  InvestorDscr = 10,
  OneYearAltDoc = 11,
  TwoYearAltDoc = 12,
  BusinessBankStmt1Mo = 13,
  BusinessBankStmt3Mos = 14,
  BusinessBankStmt6Mos = 15,
  BusinessBankStmt18Mos = 16,
  InvestorNoRatio = 17,
  PersonalBankStmt1Mo = 18,
  PersonalBankStmt3Mos = 19,
  PersonalBankStmt6Mos = 20,
  PersonalBankStmt18Mos = 21,
  PLTwoYears = 22,
  RestrictedStock = 23,
  Stated = 24,
  WrittenVOE = 25,
}

export const incomeVerificationTypeOptions: SelectItem<IncomeVerificationType>[] = [
  { label: 'Full Doc', value: IncomeVerificationType.FullDoc },
  { label: '1 Year Alt Doc', value: IncomeVerificationType.OneYearAltDoc },
  { label: '2 Year Alt Doc', value: IncomeVerificationType.TwoYearAltDoc },
  { label: '1099: 1 Year', value: IncomeVerificationType.OneYear1099 },
  { label: '1099: 2 Years', value: IncomeVerificationType.TwoYear1099 },
  { label: 'Asset Related', value: IncomeVerificationType.AssetRelated },
  { label: 'Business Bank Statement: 1 Month', value: IncomeVerificationType.BusinessBankStmt1Mo },
  { label: 'Business Bank Statement: 3 Months', value: IncomeVerificationType.BusinessBankStmt3Mos },
  { label: 'Business Bank Statement: 6 Months', value: IncomeVerificationType.BusinessBankStmt6Mos },
  { label: 'Business Bank Statement: 12 Months', value: IncomeVerificationType.BusinessBankStmt12Mos },
  { label: 'Business Bank Statement: 18 Months', value: IncomeVerificationType.BusinessBankStmt18Mos },
  { label: 'Business Bank Statement: 24 Months', value: IncomeVerificationType.BusinessBankStmt24Mos },
  { label: 'Investor - DSCR', value: IncomeVerificationType.InvestorDscr },
  { label: 'Investor - No Ratio', value: IncomeVerificationType.InvestorNoRatio },
  { label: 'Personal Bank Statement: 1 Month', value: IncomeVerificationType.PersonalBankStmt1Mo },
  { label: 'Personal Bank Statement: 3 Months', value: IncomeVerificationType.PersonalBankStmt3Mos },
  { label: 'Personal Bank Statement: 6 Months', value: IncomeVerificationType.PersonalBankStmt6Mos },
  { label: 'Personal Bank Statement: 12 Months', value: IncomeVerificationType.PersonalBankStmt12Mos },
  { label: 'Personal Bank Statement: 18 Months', value: IncomeVerificationType.PersonalBankStmt18Mos },
  { label: 'Personal Bank Statement: 24 Months', value: IncomeVerificationType.PersonalBankStmt24Mos },
  { label: 'P&L: 1 Year', value: IncomeVerificationType.PLOneYear },
  { label: 'P&L: 2 Years', value: IncomeVerificationType.PLTwoYears },
  { label: 'Restricted Stock', value: IncomeVerificationType.RestrictedStock },
  { label: 'Stated', value: IncomeVerificationType.Stated },
  { label: 'Written VOE', value: IncomeVerificationType.WrittenVOE },
];

export const incomeVerificationTypeLabel = generateLabelMap(incomeVerificationTypeOptions);

export enum LienType {
  First = 'First',
  Second = 'Second',
}

export enum ExpandedApprovalLevel {
  NotApplicable = 1,
  LevelOne = 2,
  LevelTwo = 3,
  LevelThree = 4,
  LevelFour = 5,
  LevelFive = 6,
}

export enum PrepaymentPenalty {
  None = 0,
  OneYear = 1,
  TwoYear = 2,
  ThreeYear = 3,
  FourYear = 4,
  FiveYear = 5,
}

export const prepaymentPenaltyOptions: SelectItem<PrepaymentPenalty>[] = [
  { label: 'None', value: PrepaymentPenalty.None },
  { label: '1 Year', value: PrepaymentPenalty.OneYear },
  { label: '2 Years', value: PrepaymentPenalty.TwoYear },
  { label: '3 Years', value: PrepaymentPenalty.ThreeYear },
  { label: '4 Years', value: PrepaymentPenalty.FourYear },
  { label: '5 Years', value: PrepaymentPenalty.FiveYear },
];

export const prepaymentPenaltyLabel = generateLabelMap(prepaymentPenaltyOptions);

export enum BankruptcyType {
  None = 0,
  Chapter7 = 1,
  Chapter11 = 2,
  Chapter13 = 3,
}

export const bankruptcyTypeOptions: SelectItem<BankruptcyType>[] = [
  { label: 'None', value: BankruptcyType.None },
  { label: 'Chapter 7', value: BankruptcyType.Chapter7 },
  { label: 'Chapter 11', value: BankruptcyType.Chapter11 },
  { label: 'Chapter 13', value: BankruptcyType.Chapter13 },
];

export const bankruptcyTypeLabel = generateLabelMap(bankruptcyTypeOptions);

export enum BankruptcyOutcome {
  NotApplicable = 0,
  Discharged = 1,
  Dismissed = 2,
  OpenPending = 3,
}

export const bankruptcyOutcomeOptions: SelectItem<BankruptcyOutcome>[] = [
  { label: 'Not Applicable', value: BankruptcyOutcome.NotApplicable },
  { label: 'Discharged', value: BankruptcyOutcome.Discharged },
  { label: 'Dismissed', value: BankruptcyOutcome.Dismissed },
  { label: 'Open/Pending', value: BankruptcyOutcome.OpenPending },
];

export const bankruptcyOutcomeLabel = generateLabelMap(bankruptcyOutcomeOptions);

export enum BankruptcySeasoning {
  NotApplicable = 0,
  Last1To12Months = 1,
  Last13To24Months = 2,
  Last25To36Months = 3,
  Last37To48Months = 4,
  Last49To60Months = 5,
  MoreThanFiveYearsAgo = 6,
}

export const bankruptcySeasoningOptions: SelectItem<BankruptcySeasoning>[] = [
  { label: 'Not Applicable', value: BankruptcySeasoning.NotApplicable },
  { label: '1 to 12 Months', value: BankruptcySeasoning.Last1To12Months },
  { label: '13 to 24 Months', value: BankruptcySeasoning.Last13To24Months },
  { label: '25 to 36 Months', value: BankruptcySeasoning.Last25To36Months },
  { label: '37 to 48 Months', value: BankruptcySeasoning.Last37To48Months },
  { label: '49 to 60 Months', value: BankruptcySeasoning.Last49To60Months },
  { label: '>5 Years Ago', value: BankruptcySeasoning.MoreThanFiveYearsAgo },
];

export const bankruptcySeasoningLabel = generateLabelMap(bankruptcySeasoningOptions);

export interface DropdownOption {
  id: string | number | null;
  name: string;
  value: string | number;
}

export type ExpandedGuidelinesOptionsLookup = {
  incomeVerificationTypes?: DropdownOption[];
  bankruptcyTypes?: DropdownOption[];
  bankruptcyOutcomes?: DropdownOption[];
  bankruptcySeasonings?: DropdownOption[];
  housingEventTypes?: DropdownOption[];
  housingEventSeasonings?: DropdownOption[];
};

export enum HousingEventType {
  None = 0,
  DeedInLieu = 1,
  Forbearance = 2,
  Foreclosure = 3,
  Modification = 4,
  ShortSale = 5,
}

export const housingEventTypeOptions: SelectItem<HousingEventType>[] = [
  { label: 'None', value: HousingEventType.None },
  { label: 'Deed-In-Lieu', value: HousingEventType.DeedInLieu },
  { label: 'Forbearance', value: HousingEventType.Forbearance },
  { label: 'Foreclosure', value: HousingEventType.Foreclosure },
  { label: 'Modification', value: HousingEventType.Modification },
  { label: 'Short Sale', value: HousingEventType.ShortSale },
];

export const housingEventTypeLabel = generateLabelMap(housingEventTypeOptions);

export enum HousingEventSeasoning {
  NotApplicable = 0,
  Last1To12Months = 1,
  Last13To24Months = 2,
  Last25To36Months = 3,
  Last37To48Months = 4,
  Last49To60Months = 5,
  MoreThanFiveYearsAgo = 6,
}

export const housingEventSeasoningOptions: SelectItem<HousingEventSeasoning>[] = [
  { label: 'Not Applicable', value: HousingEventSeasoning.NotApplicable },
  { label: '1 to 12 Months', value: HousingEventSeasoning.Last1To12Months },
  { label: '13 to 24 Months', value: HousingEventSeasoning.Last13To24Months },
  { label: '25 to 36 Months', value: HousingEventSeasoning.Last25To36Months },
  { label: '37 to 48 Months', value: HousingEventSeasoning.Last37To48Months },
  { label: '49 to 60 Months', value: HousingEventSeasoning.Last49To60Months },
  { label: '>5 Years Ago', value: HousingEventSeasoning.MoreThanFiveYearsAgo },
];

export const housingEventSeasoningLabel = generateLabelMap(housingEventSeasoningOptions);

export enum LOCompensationPricing {
  YesLenderPaid = 1,
  NoBuyerPaid = 2,
  NoLenderPaid = 3,
}

export enum ProductType {
  StandardProducts = 1,
  AffordableProducts = 2,
  HARP = 4,
  HeroProducts = 8,
  StudentLoanCORefi = 16,
  RenovationAndRehabilitation = 32,
  BondAndHFA = 64,
  HUDspecialty = 128,
  USDAStreamline = 256,
  ExpandedGuidelines = 512,
}

// Note: these are ordered as they should appear in the UI, not in enum value order.
export const productTypeOptionsScenarios: SelectItem<ProductType>[] = [
  { label: 'Standard', value: ProductType.StandardProducts },
  { label: 'Affordable', value: ProductType.AffordableProducts },
  { label: 'HARP', value: ProductType.HARP },
  { label: 'Hero/Champion', value: ProductType.HeroProducts },
  { label: 'HFA/Bond', value: ProductType.BondAndHFA },
  { label: 'HUD Specialty', value: ProductType.HUDspecialty },
  { label: 'Reno/Rehab', value: ProductType.RenovationAndRehabilitation },
  { label: 'Student Loan C.O. Refi', value: ProductType.StudentLoanCORefi },
  { label: 'USDA Streamline', value: ProductType.USDAStreamline },
  { label: 'Expanded Guidelines', value: ProductType.ExpandedGuidelines },
];

// Note: this is duplicate of above but in different order.
// Necessary because on Pricing, the list should be ordered top to bottom not left to right.
export const productTypeOptionsPricing: SelectItem<ProductType>[] = [
  { label: 'Standard', value: ProductType.StandardProducts },
  { label: 'HUD Specialty', value: ProductType.HUDspecialty },
  { label: 'Affordable', value: ProductType.AffordableProducts },
  { label: 'Reno/Rehab', value: ProductType.RenovationAndRehabilitation },
  { label: 'HARP', value: ProductType.HARP },
  { label: 'Student Loan C.O. Refi', value: ProductType.StudentLoanCORefi },
  { label: 'Hero/Champion', value: ProductType.HeroProducts },
  { label: 'USDA Streamline', value: ProductType.USDAStreamline },
  { label: 'HFA/Bond', value: ProductType.BondAndHFA },
  { label: 'Expanded Guidelines', value: ProductType.ExpandedGuidelines },
];

export const productTypeLabel = generateLabelMap(productTypeOptionsScenarios);

export interface PriceAdjustment {
  adjustment?: string;
  points?: number;
  rate?: number;
  margin?: number;
}

export interface PricingResult {
  guid?: string;
  scenarioCriteriaGUID?: string;
  scenarioName?: string;
  amortizationType?: AmortizationType | IneligibleAmortizationType;
  armFixedTerm?: ArmFixedTerm;
  armSubsequentChangePeriod?: ArmSubsequentChangePeriod;
  armFirstCap?: number;
  armPeriodicCap?: number;
  armRelativeCap?: number;
  discountRebatePercent?: number;
  discountRebateAmount?: number;
  interestRate?: number;
  lastPriced?: string;
  loanTerm?: LoanTerm;
  lockPeriod?: number;
  monthlyPI?: number;
  monthlyPITI?: number;
  mortgageType?: MortgageType;
  price?: number;
  productID?: number;
  productName?: string;
  productCode?: string;
  productType?: ProductType;
  notesAndAdvisories?: string[];
  searchID?: string;
  lockExpirationDate?: string;
  parRate?: number;
  parPrice?: number;
  parLock?: number;
  priceAdjustments?: PriceAdjustment[];
  isClosestToPar?: boolean;
  hasRatesheetBeenFetched?: boolean; // Sent to the backend to indicate record needs product details retrieved
  rateSheetResponse?: RateSheetResponse;
  street?: string;
  city?: string;
  communityLending?: boolean;
  mhAdvantage?: boolean;
  communityAffordableSecond?: boolean;
  noOfFinancedProperties?: number;
  bestXMi?: boolean;
  pmiType?: PmiType | number;
  miProvider?: number;
  monthlyPremiumAmount?: number;
  monthlyPremiumRate?: number;
  coveragePercentage?: number;
  housingExpenses?: HousingExpense[];
  lockPeriodSelection?: Record<number, number>;
  // Ineligible Fields
  ineligible?: boolean;
  ineligibleReason?: string[];
  ineligibleStatus?: IneligibleStatus;
  amortizationTerm?: AmortizationTerm;
  investorId?: number;
  investor?: string;
  loanType?: LoanType;
  pendingUpdate?: boolean;
  productId?: number;
  guidelines?: Guidelines[];
  constructionPeriodInterestRate?: number;
  constructionTermMonths?: number;
  constructionPermanentFinancingLoanTerm?: number;
  constructionPermanentFinancingAmortizationTerm?: number;
  collectMonthlyMIDuringConstructionTerm?: boolean;
  // Expired
  expired?: boolean;
  coveragePercent?: number | null; // This is BestX MI coveragePercentage
  fetchMiOnSave?: boolean;
  upfrontPremiumRate?: number | null;
  upfrontPremiumAmount?: number | null;
  upfrontPremiumAmountFinanced?: number | null;
  upfrontPremiumAmountCash?: number | null;
  // UI Only Fields
  groupAll?: string;
  isSelected?: boolean;
  viewRateSheet?: boolean;
  rateSheetData?: PricingResult[];
  isRateSheet?: boolean;
  isRateSheetLoading?: boolean;
  hasSelectedRateSheetItems?: boolean;
  hover?: boolean;
  isSelectedForComparison?: boolean;
  isDesired?: boolean;
  selectedLockPeriod?: number;
  selectedLockPeriodExpirationDate?: string;
  ineligibleReasonDisplay?: string;
  loanTermDisplay?: string;
  loanTermAmortizationDisplay?: string;
  lockPeriods?: number[];
  mortgageTypeDisplay?: string;
  amortizationTypeDisplay?: string;
  viewScenarioOptimizerLoadingIcon?: boolean;
  housingExpensesApplied?: boolean;
  scenarioOptimizerSubPanelData?: Suggestion[];
  scenarioOptimizerHasBeenRun?: boolean;
  displayScenarioOptimizerSubPanel?: boolean;
  aiRunBtnEnabled?: boolean;
  governmentMiRerun?: boolean; // UI ONLY
  productNameTruncated?: boolean;
  rowHasBeenExpanded?: boolean;
  fullName?: string;
  cardsAndOtherDebtPaidOff?: number;
  earnestMoneyDeposit?: number;
  mortgageLoansToBePaid?: number;
  lumpSumSellerCredit?: number;
  selectedRateRows?: any;
  selectedRateRowsDisplay?: PricingResult[];
}

export interface PrefillScenarioOptimizerPayload {
  suggestion: Suggestion;
  initialFormValue: PricingInputFormModel;
}

export interface RunScenarioPricingResponse {
  guid?: string;
  searchIDs?: string[];
  results?: PricingResult[];
  searchInfoByMortgageType?: Record<MortgageType, SearchResultInformation>;
}

export interface ProductDetails {
  armFirstCap?: number;
  armPeriodicCap?: number;
  armRelativeCap?: number;
  expirationDateByLockPeriod?: { [key: number]: string };
  notesAndAdvisories?: string[];
  priceAdjustments?: PriceAdjustment[];
}

export interface RateSheetResponse {
  productDetails?: ProductDetails;
  scenarios?: PricingResult[];
}

export interface PricingRunData {
  scenarioCriteriaGuid?: string;
  searchIDs?: string[];
  ineligibleProducts?: IneligibleProductsResponse;
  pricingResults?: PricingResult[];
  scenarioCriteria?: ScenarioCriteriaModel;
  scenarioGuid?: string;
  isRerun?: boolean;
  isNew?: boolean;
  tabId?: string;
  searchInfoByMortgageType?: Record<MortgageType, SearchResultInformation>;
  miConfig?: MiConfig;
  selectedGrouping?: string;
  activePaymentTab?: PaymentTabs;
  activeTableTab?: TableTab;
  selectedCount?: number;
  selectedLockPeriodBestPricing?: number;
  selectedLockPeriodCompare?: number;
  lockPeriodPageIndex?: number;
  from?: AddScenarioPricingTabFrom;
}

export interface MiConfig {
  miQuotesFixed?: MiQuoteViewModel;
  fixedRerunBestXMiResponse?: Record<string, RunBestXMiResult>;
  fixedRunBestXMiResponse?: RunBestXMiResponse;
  miQuotesARM?: MiQuoteViewModel;
  armRunBestXMiResponse?: Record<string, RunBestXMiResult>;
  armRerunBestXMiResponse?: Record<string, RunBestXMiResult>;
  displayMiQuoteFixed?: boolean;
  selectedPmiTypeBestXMI?: PmiType;
  isPmi?: boolean;
  showPmiType?: boolean;
}

export interface ProductGroupSearchResponse {
  searchIDs?: string[];
  results?: GroupPricingResult[];
  lockPeriod?: DesiredLockPeriod | number;
  sortField?: string;
  sortOrder?: number;
}

export interface BestPricing extends ProductGroupSearchResponse {
  lockPeriodExpirationDate: string;
}

export interface GroupPricingResult extends PricingResult {
  productGroupID?: string; //For ProductGroupSearch
  productDetails?: ProductDetails; // For ProductGroupSearch
  productGroupDetailResponse?: ProductGroupRatesheet[] | PricingResult[]; // For ProductGroupSearch
}

export interface ProductGroupRatesheetsResponse {
  productGroupId: string;
  allScenarios?: PricingResult[]; // For UI only
  ratesheets: ProductGroupRatesheet[];
}

export interface ProductGroupRatesheet extends RateSheetResponse {
  productId?: number | string; // For Product Group Ratesheet
}

export enum MiMethod {
  BestXMI = 1,
  Manual = 2,
}

export enum IneligibleStatus {
  Disqualified = 0,
  Eligible = 1,
  Unknown = 2,
}

export enum AmortizationTerm {
  ThirtyYear = 1,
  TwentyFiveYear = 2,
  TwentyYear = 3,
  FifteenYear = 4,
  TenYear = 5,
  SevenYear = 6,
  FiveYear = 7,
  FortyYear = 8,
  FourYear = 9,
  TwoYear = 10,
  OneYear = 11,
  SixYear = 12,
  EightYear = 13,
  NineYear = 14,
  TwelveYear = 15,
  ThreeYear = 16,
}

export enum IneligibleAmortizationType {
  StraightLine = 0,
  DecliningBalance = 1,
  Annuity = 2,
  Bullet = 3,
  NegativeAmortization = 4,
}

export enum LoanType {
  Conforming = 1,
  NonConforming = 2,
  FHA = 3,
  VA = 4,
  Conventional = 5,
  HELOC = 6,
  USDARuralHousing = 7,
}

export interface Guidelines {
  documentDescription?: string;
}

export interface ModelState {
  additionalProperties?: string[];
}

export interface ResponseMessage {
  code?: string;
  message?: string;
  modelState?: ModelState;
}

export interface IneligibleProductsResponse {
  searchId?: string;
  amiPercentage?: number;
  ineligibleProducts?: PricingResult[];
  messages?: ResponseMessage[];
}

// DTO used for "/scenarios/save" and "/scenarios/saveandreplace" endpoints
export interface SaveScenariosRequest {
  scenarios: PricingResult[];
  criteria: ScenarioCriteriaModel;
}
export interface SingleCompanyTableRow {
  name?: string;
  monthly?: number | string;
  split?: number | string;
  upfront?: number | string;
  lpmi?: number | string;
}

export enum MiPremiumField {
  UpfrontPremium = 'Upfront Premium',
  UpfrontPremiumRate = 'Upfront Premium Rate',
  MonthlyPremium = 'Monthly Premium',
  MonthlyPremiumRate = 'Monthly Premium Rate',
}

export const singleCompanyTableRowInitial = [
  { name: MiPremiumField.UpfrontPremium, monthly: '—', split: '—', upfront: '—', lpmi: '—' },
  { name: MiPremiumField.UpfrontPremiumRate, monthly: '—', split: '—', upfront: '—', lpmi: '—' },
  { name: MiPremiumField.MonthlyPremium, monthly: '—', split: '—', upfront: '—', lpmi: '—' },
  { name: MiPremiumField.MonthlyPremiumRate, monthly: '—', split: '—', upfront: '—', lpmi: '—' },
];

export type FieldMapping = {
  rowName: MiPremiumField;
  cellKey: Exclude<keyof SingleCompanyTableRow, 'name'>;
  quoteKey: keyof Pick<MiQuote, 'firstMonthlyPremiumAmount' | 'firstMonthlyPremiumRate' | 'premiumUpfrontAmount' | 'premiumUpfrontPercent'>; // …from which MiQuote field.
};

export type Formatter = 'currency' | 'percent';

export const configMap: Record<
  PmiType,
  {
    rowName: MiPremiumField;
    targetProp: keyof SingleCompanyTableRow;
    quoteField: keyof MiQuote;
    format: Formatter;
  }[]
> = {
  [PmiType.BorrowerMonthlyNonRefundableConstant]: [
    {
      rowName: MiPremiumField.MonthlyPremium,
      targetProp: 'monthly',
      quoteField: 'firstMonthlyPremiumAmount',
      format: 'currency',
    },
    {
      rowName: MiPremiumField.MonthlyPremiumRate,
      targetProp: 'monthly',
      quoteField: 'firstMonthlyPremiumRate',
      format: 'percent',
    },
  ],
  [PmiType.SplitConstantUpfront100]: [
    {
      rowName: MiPremiumField.MonthlyPremium,
      targetProp: 'split',
      quoteField: 'firstMonthlyPremiumAmount',
      format: 'currency',
    },
    {
      rowName: MiPremiumField.MonthlyPremiumRate,
      targetProp: 'split',
      quoteField: 'firstMonthlyPremiumRate',
      format: 'percent',
    },
    {
      rowName: MiPremiumField.UpfrontPremium,
      targetProp: 'split',
      quoteField: 'premiumUpfrontAmount',
      format: 'currency',
    },
    {
      rowName: MiPremiumField.UpfrontPremiumRate,
      targetProp: 'split',
      quoteField: 'premiumUpfrontPercent',
      format: 'percent',
    },
  ],
  [PmiType.BorrowerSingleNonRefundable]: [
    {
      rowName: MiPremiumField.UpfrontPremium,
      targetProp: 'upfront',
      quoteField: 'premiumUpfrontAmount',
      format: 'currency',
    },
    {
      rowName: MiPremiumField.UpfrontPremiumRate,
      targetProp: 'upfront',
      quoteField: 'premiumUpfrontPercent',
      format: 'percent',
    },
  ],
  [PmiType.LenderSingleNonRefundable]: [
    {
      rowName: MiPremiumField.UpfrontPremium,
      targetProp: 'lpmi',
      quoteField: 'premiumUpfrontAmount',
      format: 'currency',
    },
    {
      rowName: MiPremiumField.UpfrontPremiumRate,
      targetProp: 'lpmi',
      quoteField: 'premiumUpfrontPercent',
      format: 'percent',
    },
  ],
};

export enum MIProvider {
  BestX = 'Best Ex (All Providers)',
  ArchMI = 1,
  Enact = 2,
  Essent = 4,
  MGIC = 8,
  NationalMI = 16,
  Radian = 32,
}

export type MIProviderWithoutBestEx = Exclude<MIProvider, MIProvider.BestX>;

export const MIProvidersMap: Record<MIProvider, string> = {
  [MIProvider.BestX]: 'Best Ex (All Providers)',
  [MIProvider.ArchMI]: 'Arch MI',
  [MIProvider.Enact]: 'Enact',
  [MIProvider.Essent]: 'Essent',
  [MIProvider.MGIC]: 'MGIC',
  [MIProvider.NationalMI]: 'National MI',
  [MIProvider.Radian]: 'Radian',
};

export const { [MIProvider.BestX]: _removed, ...MIProvidersMapFiltered } = MIProvidersMap;

export const miProvidersOptions: SelectItem<MIProvider>[] = [
  { label: 'Best Ex (All Providers)', value: MIProvider.BestX },
  { label: 'Arch MI', value: MIProvider.ArchMI },
  { label: 'Enact', value: MIProvider.Enact },
  { label: 'Essent', value: MIProvider.Essent },
  { label: 'MGIC', value: MIProvider.MGIC },
  { label: 'National MI', value: MIProvider.NationalMI },
  { label: 'Radian', value: MIProvider.Radian },
];

export const miProvidersOptionsFiltered = miProvidersOptions.filter((mp) => mp.value !== MIProvider.BestX);

export const singleCompanyMiQuotesTemplate: Record<MIProviderWithoutBestEx, SingleCompanyTableRow[]> = {
  [MIProvider.ArchMI]: [...singleCompanyTableRowInitial],
  [MIProvider.Enact]: [...singleCompanyTableRowInitial],
  [MIProvider.Essent]: [...singleCompanyTableRowInitial],
  [MIProvider.MGIC]: [...singleCompanyTableRowInitial],
  [MIProvider.NationalMI]: [...singleCompanyTableRowInitial],
  [MIProvider.Radian]: [...singleCompanyTableRowInitial],
};

export const miProviderWithoutBestExOptions: SelectItem<MIProvider | number>[] = [
  { label: 'Select Provider', value: null },
  ...miProvidersOptionsFiltered,
];

export const MiProviderImgMap: Record<MIProviderWithoutBestEx, string> = {
  [MIProvider.ArchMI]: 'assets/images/ArchMI.png',
  [MIProvider.NationalMI]: 'assets/images/NationalMI.png',
  [MIProvider.MGIC]: 'assets/images/MGIC.png',
  [MIProvider.Enact]: 'assets/images/Enact.png',
  [MIProvider.Essent]: 'assets/images/EssentMI.png',
  [MIProvider.Radian]: 'assets/images/Radian.png',
};

export enum CoverageType {
  Standard = 1,
  StandardReducedCoverage = 2,
  Affordable = 3,
  AffordableReducedCoverage = 4,
  HfaUnlessStandardCoverageRequired = 5,
  NonStandard = 6,
}

export const CoverageTypeMap: Record<CoverageType, string> = {
  [CoverageType.Standard]: 'Standard GSE',
  [CoverageType.StandardReducedCoverage]: 'Standard GSE Reduced Coverage',
  [CoverageType.Affordable]: 'Affordable',
  [CoverageType.AffordableReducedCoverage]: 'Affordable Reduced Coverage',
  [CoverageType.HfaUnlessStandardCoverageRequired]: 'HFA - unless Standard Coverage Required',
  [CoverageType.NonStandard]: 'Non-Standard',
};

export const coverageTypeOptions: SelectItem<CoverageType>[] = [{ label: 'Standard GSE', value: CoverageType.Standard }];

export enum CoveragePercentage {
  ThirtyFive = 1,
  Thirty = 2,
  TwentyFive = 3,
  Eighteen = 4,
  Sixteen = 5,
  Twelve = 6,
  Six = 7,
}

const _coverageValues: Record<CoveragePercentage, number> = {
  [CoveragePercentage.ThirtyFive]: 35,
  [CoveragePercentage.Thirty]: 30,
  [CoveragePercentage.TwentyFive]: 25,
  [CoveragePercentage.Eighteen]: 18,
  [CoveragePercentage.Sixteen]: 16,
  [CoveragePercentage.Twelve]: 12,
  [CoveragePercentage.Six]: 6,
};

export const coveragePercentageToPercent = _coverageValues;

export const CoveragePercentageMap: Record<CoveragePercentage, string> = Object.fromEntries(
  Object.entries(_coverageValues).map(([key, val]) => [Number(key), `${val}%`]),
) as Record<CoveragePercentage, string>;

export const coveragePercentageOptions: SelectItem<CoveragePercentage>[] = [{ label: '35%', value: CoveragePercentage.ThirtyFive }];

export interface PricingBorrower {
  isPrimary?: boolean;
  fico: number;
  selfEmployed: boolean;
  hadForeclosure: boolean;
  hadBankruptcy: boolean;
}

export interface HousingExpense {
  amount?: number;
  calcMethod?: HousingExpenseCalcMethod;
  factor?: number;
  frequency?: FrequencyType;
  type?: HousingExpenseType;

  // Disbursement schedule
  disbursementSchedule?: DisbursementSchedule;
  nextDisbursementMonth?: Month[];
  nextDisbursementYear?: number;

  // Escrowed information
  escrowed?: boolean;
  escrowWaiver?: EscrowWaiver;

  // Calculated amounts (FE computes, BE stores for audit)
  monthlyAmount?: number;
  escrowMonths?: number;
  escrowsTotal?: number;
  prepaidsMonths?: number;
  prepaidsTotal?: number;

  /* UI only fields */
  field?: AdditionalHousingExpenseField;
}

export enum HousingExpenseCalcMethod {
  Factor = 1,
  Frequency = 2,
}

export enum FrequencyType {
  Annually = 1,
  SemiAnnually = 2,
  Quarterly = 3,
  Monthly = 4,
}

export enum DisbursementSchedule {
  Annually = 1,
  SemiAnnually = 2,
  Quarterly = 3,
  Monthly = 4,
  OtherAnnually = 5,
}

export enum EscrowWaiver {
  NotWaived = 0,
  WaiveAll = 1,
  WaiveTaxesOnly = 2,
  WaiveInsuranceOnly = 3,
}

export enum Month {
  January = 1,
  February = 2,
  March = 3,
  April = 4,
  May = 5,
  June = 6,
  July = 7,
  August = 8,
  September = 9,
  October = 10,
  November = 11,
  December = 12,
}

export const HousingExpenseFrequencyOptions: readonly SelectItem<FrequencyType>[] = [
  { label: 'Monthly', value: FrequencyType.Monthly },
  { label: 'Quarterly', value: FrequencyType.Quarterly },
  { label: 'Semi-Annually', value: FrequencyType.SemiAnnually },
  { label: 'Annually', value: FrequencyType.Annually },
];

/** Plain-object shape matching HousingExpenseFormGroup values (no FormControl wrappers). */
export interface HousingExpenseFormValue {
  type: HousingExpenseType | null;
  amount: number | null;
  calcMethod: HousingExpenseCalcMethod | null;
  factor: number | null;
  frequency: FrequencyType;
  field: AdditionalHousingExpenseField | null;
  disbursementSchedule: DisbursementSchedule | null;
  escrowed: boolean;
  nextDisbursementMonth: Month[] | null;
  nextDisbursementYear: number | null;
  monthlyAmount: number | null;
  escrowMonths: number | null;
  escrowsTotal: number | null;
  prepaidsMonths: number | null;
  prepaidsTotal: number | null;
}

export function createDefaultHousingExpenseFormValue(type: HousingExpenseType | null = null): HousingExpenseFormValue {
  return {
    type,
    amount: null,
    calcMethod: null,
    factor: null,
    frequency: FrequencyType.Annually,
    field: null,
    disbursementSchedule: null,
    escrowed: false,
    nextDisbursementMonth: null,
    nextDisbursementYear: null,
    monthlyAmount: null,
    escrowMonths: null,
    escrowsTotal: null,
    prepaidsMonths: null,
    prepaidsTotal: null,
  };
}

export interface HousingExpenseAppSetting {
  HomeownersInsurance?: HousingExpenseAppSettingDetails;
  PropertyTaxes?: HousingExpenseAppSettingDetails;
}

export interface HousingExpenseAppSettingDetails {
  rate?: number;
}

export enum AdditionalHousingExpenseField {
  Assessment = 'assessment',
  CityPropertyTax = 'cityPropertyTax',
  EarthquakeInsurance = 'earthquakeInsurance',
  FloodInsurance = 'floodInsurance',
  HazardInsurance = 'hazardInsurance',
  SchoolPropertyTax = 'schoolPropertyTax',
  TownPropertyTax = 'townPropertyTax',
  VillagePropertyTax = 'villagePropertyTax',
  WindstormInsurance = 'windstormInsurance',
  Other = 'other',
  AllAvailableExpensesHaveBeenAdded = 'AllAvailableExpensesHaveBeenAdded',
}

export enum HousingExpenseType {
  PropertyTaxes = 1,
  HomeownersInsurance = 2,
  Hoa = 3,
  FloodInsurance = 4,
  Assessment = 5,
  CityPropertyTax = 6,
  EarthquakeInsurance = 7,
  HazardInsurance = 8,
  Other = 9,
  SchoolPropertyTax = 10,
  TownPropertyTax = 11,
  VillagePropertyTax = 12,
  WindstormInsurance = 13,
}

export const AdditionalHousingExpenseTypes = new Set([
  HousingExpenseType.Assessment,
  HousingExpenseType.CityPropertyTax,
  HousingExpenseType.EarthquakeInsurance,
  HousingExpenseType.FloodInsurance,
  HousingExpenseType.HazardInsurance,
  HousingExpenseType.Other,
  HousingExpenseType.SchoolPropertyTax,
  HousingExpenseType.TownPropertyTax,
  HousingExpenseType.VillagePropertyTax,
  HousingExpenseType.WindstormInsurance,
]);

export const AdditionalHousingExpenseFieldMap: Partial<Record<HousingExpenseType, AdditionalHousingExpenseField>> = {
  [HousingExpenseType.Assessment]: AdditionalHousingExpenseField.Assessment,
  [HousingExpenseType.CityPropertyTax]: AdditionalHousingExpenseField.CityPropertyTax,
  [HousingExpenseType.EarthquakeInsurance]: AdditionalHousingExpenseField.EarthquakeInsurance,
  [HousingExpenseType.FloodInsurance]: AdditionalHousingExpenseField.FloodInsurance,
  [HousingExpenseType.HazardInsurance]: AdditionalHousingExpenseField.HazardInsurance,
  [HousingExpenseType.Other]: AdditionalHousingExpenseField.Other,
  [HousingExpenseType.SchoolPropertyTax]: AdditionalHousingExpenseField.SchoolPropertyTax,
  [HousingExpenseType.TownPropertyTax]: AdditionalHousingExpenseField.TownPropertyTax,
  [HousingExpenseType.VillagePropertyTax]: AdditionalHousingExpenseField.VillagePropertyTax,
  [HousingExpenseType.WindstormInsurance]: AdditionalHousingExpenseField.WindstormInsurance,
} as const satisfies Partial<Record<HousingExpenseType, AdditionalHousingExpenseField>>;

export const AdditionalHousingExpenseTypeMap = {
  [AdditionalHousingExpenseField.Assessment]: HousingExpenseType.Assessment,
  [AdditionalHousingExpenseField.CityPropertyTax]: HousingExpenseType.CityPropertyTax,
  [AdditionalHousingExpenseField.EarthquakeInsurance]: HousingExpenseType.EarthquakeInsurance,
  [AdditionalHousingExpenseField.FloodInsurance]: HousingExpenseType.FloodInsurance,
  [AdditionalHousingExpenseField.HazardInsurance]: HousingExpenseType.HazardInsurance,
  [AdditionalHousingExpenseField.Other]: HousingExpenseType.Other,
  [AdditionalHousingExpenseField.SchoolPropertyTax]: HousingExpenseType.SchoolPropertyTax,
  [AdditionalHousingExpenseField.TownPropertyTax]: HousingExpenseType.TownPropertyTax,
  [AdditionalHousingExpenseField.VillagePropertyTax]: HousingExpenseType.VillagePropertyTax,
  [AdditionalHousingExpenseField.WindstormInsurance]: HousingExpenseType.WindstormInsurance,
} as const satisfies Partial<Record<AdditionalHousingExpenseField, HousingExpenseType>>;

export const AdditionalHousingExpenseLabelMap: Record<AdditionalHousingExpenseField, string> = {
  [AdditionalHousingExpenseField.Assessment]: 'Assessment',
  [AdditionalHousingExpenseField.CityPropertyTax]: 'City Property Tax',
  [AdditionalHousingExpenseField.EarthquakeInsurance]: 'Earthquake Insurance',
  [AdditionalHousingExpenseField.FloodInsurance]: 'Flood Insurance',
  [AdditionalHousingExpenseField.HazardInsurance]: 'Hazard Insurance',
  [AdditionalHousingExpenseField.SchoolPropertyTax]: 'School Property Tax',
  [AdditionalHousingExpenseField.TownPropertyTax]: 'Town Property Tax',
  [AdditionalHousingExpenseField.VillagePropertyTax]: 'Village Property Tax',
  [AdditionalHousingExpenseField.WindstormInsurance]: 'Windstorm Insurance',
  [AdditionalHousingExpenseField.Other]: 'Other',
  [AdditionalHousingExpenseField.AllAvailableExpensesHaveBeenAdded]: 'All available expenses have been added',
} as const;

export const AdditionalHousingExpenseDefs: AdditionalHousingExpenseDef[] = [
  { label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.Assessment], field: AdditionalHousingExpenseField.Assessment },
  { label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.CityPropertyTax], field: AdditionalHousingExpenseField.CityPropertyTax },
  {
    label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.EarthquakeInsurance],
    field: AdditionalHousingExpenseField.EarthquakeInsurance,
  },
  { label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.FloodInsurance], field: AdditionalHousingExpenseField.FloodInsurance },
  { label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.HazardInsurance], field: AdditionalHousingExpenseField.HazardInsurance },
  { label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.Other], field: AdditionalHousingExpenseField.Other },
  {
    label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.SchoolPropertyTax],
    field: AdditionalHousingExpenseField.SchoolPropertyTax,
  },
  { label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.TownPropertyTax], field: AdditionalHousingExpenseField.TownPropertyTax },
  {
    label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.VillagePropertyTax],
    field: AdditionalHousingExpenseField.VillagePropertyTax,
  },
  {
    label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.WindstormInsurance],
    field: AdditionalHousingExpenseField.WindstormInsurance,
  },
  {
    label: AdditionalHousingExpenseLabelMap[AdditionalHousingExpenseField.AllAvailableExpensesHaveBeenAdded],
    field: AdditionalHousingExpenseField.AllAvailableExpensesHaveBeenAdded,
  },
] as const;

export type AdditionalHousingExpenseDef = { label: string; field: AdditionalHousingExpenseField };

export type AdditionalHousingExpensesItem = {
  label: string;
  field: AdditionalHousingExpenseField;
  command: () => void;
};

export type Predicate<T> = (value: T[keyof T], key: keyof T) => boolean;

export type StripeBit = 0 | 1;

export type RowWithStripe = PricingResult & { _stripe?: StripeBit };

export type HousingExpenseMenuItem = MenuItem & { field: AdditionalHousingExpenseField };

export type HousingExpenseFormGroup = FormGroup<{
  type: FormControl<HousingExpenseType | null>;
  amount: FormControl<number | null>;
  calcMethod: FormControl<HousingExpenseCalcMethod | null>;
  factor: FormControl<number | null>;
  frequency: FormControl<FrequencyType>;
  field: FormControl<AdditionalHousingExpenseField | null>;
  disbursementSchedule: FormControl<DisbursementSchedule | null>;
  escrowed: FormControl<boolean>;
  nextDisbursementMonth: FormControl<Month[] | null>;
  nextDisbursementYear: FormControl<number | null>;
  monthlyAmount: FormControl<number | null>;
  escrowMonths: FormControl<number | null>;
  escrowsTotal: FormControl<number | null>;
  prepaidsMonths: FormControl<number | null>;
  prepaidsTotal: FormControl<number | null>;
}>;

export type ExpenseKey = 'PropertyTaxes' | 'HomeownersInsurance';

export const OtherHousingExpenses = [HousingExpenseType.Assessment, HousingExpenseType.Other];

export const PropertyTaxesHousingExpenses = [
  HousingExpenseType.PropertyTaxes,
  HousingExpenseType.CityPropertyTax,
  HousingExpenseType.SchoolPropertyTax,
  HousingExpenseType.TownPropertyTax,
  HousingExpenseType.VillagePropertyTax,
];

export const SupplementalPropertyInsuranceHousingExpenses = [
  HousingExpenseType.EarthquakeInsurance,
  HousingExpenseType.FloodInsurance,
  HousingExpenseType.HazardInsurance,
  HousingExpenseType.WindstormInsurance,
];

export enum PrepaymentStructure {
  NotApplicable = 0,
  FlatFivePercent = 1,
  FlatFourPercent = 2,
  FlatThreePercent = 3,
  FlatTwoPercent = 4,
  FlatOnePercent = 5,
  SixMonthsInterest = 6,
  FiveToOnePercent = 7,
  FourToOnePercent = 8,
  ThreeToOnePercent = 9,
  TwoToOnePercent = 10,
  OnePercent = 11,
}

export const prepaymentStructureOptions: SelectItem<PrepaymentStructure>[] = [
  { label: 'Not Applicable', value: PrepaymentStructure.NotApplicable },
  { label: 'Flat - 5%', value: PrepaymentStructure.FlatFivePercent },
  { label: 'Flat - 4%', value: PrepaymentStructure.FlatFourPercent },
  { label: 'Flat - 3%', value: PrepaymentStructure.FlatThreePercent },
  { label: 'Flat - 2%', value: PrepaymentStructure.FlatTwoPercent },
  { label: 'Flat - 1%', value: PrepaymentStructure.FlatOnePercent },
  { label: '6 Months Interest', value: PrepaymentStructure.SixMonthsInterest },
  { label: '5% 4% 3% 2% 1%', value: PrepaymentStructure.FiveToOnePercent },
  { label: '4% 3% 2% 1%', value: PrepaymentStructure.FourToOnePercent },
  { label: '3% 2% 1%', value: PrepaymentStructure.ThreeToOnePercent },
  { label: '2% 1%', value: PrepaymentStructure.TwoToOnePercent },
  { label: '1%', value: PrepaymentStructure.OnePercent },
];

export const prepaymentStructureLabel = generateLabelMap(prepaymentStructureOptions);

export enum SectionOfAct {
  Section203b = 1,
  Section203b2 = 2,
  Section203b251 = 3,
  Section203k = 4,
  Section203k251 = 5,
  Section221d2 = 6,
  Section221d2251 = 7,
  Section234c = 8,
  Section234c251 = 9,
  Section184 = 10,
  Section247 = 11,
  Section203h = 12,
}

export const sectionOfActOptions: SelectItem<SectionOfAct>[] = [
  { label: 'Select', value: null },
  { label: '203(b)', value: SectionOfAct.Section203b },
  { label: '203(b)2', value: SectionOfAct.Section203b2 },
  { label: '203(b)/251', value: SectionOfAct.Section203b251 },
  { label: '203(k)', value: SectionOfAct.Section203k },
  { label: '203(k)/251', value: SectionOfAct.Section203k251 },
  { label: '221(d)2', value: SectionOfAct.Section221d2 },
  { label: '221(d)(2)/251', value: SectionOfAct.Section221d2251 },
  { label: '234(c)', value: SectionOfAct.Section234c },
  { label: '234(c)/251', value: SectionOfAct.Section234c251 },
  { label: '184', value: SectionOfAct.Section184 },
  { label: '247', value: SectionOfAct.Section247 },
  { label: '203(h)', value: SectionOfAct.Section203h },
];

export const sectionOfActLabel = generateLabelMap(sectionOfActOptions);

export const usdaCaseAssignedMapping: Record<UsdaCaseAssigned, string> = {
  [UsdaCaseAssigned.OnOrBefore09302011]: '09/30/2011',
  [UsdaCaseAssigned.Between10012011And12062011]: '12/06/2011',
  [UsdaCaseAssigned.Between12072011And09302012]: '09/30/2012',
  [UsdaCaseAssigned.Between10012012And09302014]: '09/30/2014',
  [UsdaCaseAssigned.Between10012014And09302015]: '09/30/2015',
  [UsdaCaseAssigned.Between10012015And09302016]: '09/30/2016',
  [UsdaCaseAssigned.OnOrAfter10012016]: '10/01/2016',
};

export type PricingInputForm = PricingInputFormModel;

export interface RerunPricingTitleInfo {
  vendor?: string;
  pmiType?: string;
  percentText?: string;
  termText?: string;
  rate?: number;
}

export interface RerunPricingEmit {
  initialFormValue: PricingInputFormModel;
  titleObjForRerunPricing: RerunPricingTitleInfo;
}

export interface RequiredServicesRequest {
  mortgageType?: MortgageType;
  loanPurpose?: LoanPurpose;
  state?: string;
  county?: string;
  propertyType?: PropertyType;
  isPUD?: boolean;
  /** Whether an appraisal waiver was granted */
  appraisalWaiver?: boolean;
}

export interface RequiredServicesResponse {
  requestId?: string;
  /** Indicates whether all rules passed successfully */
  success?: boolean;
  /** Indicates whether rule failures can be overridden */
  overridable?: boolean;
  /** Messages from rule evaluation (informational, warnings, failures) */
  messages?: RequiredServiceMessage[];
  /** Errors that occurred during rule evaluation */
  errors?: RequiredServiceMessage[];
}

export interface RunFeesData {
  scenarioGUID: string;
  productName: string;
  loanPurpose: LoanPurpose | null;
  state: string | null;
  mortgageType: MortgageType | null;
  street: string;
  propertyType: PropertyType | null;
  numberOfUnits: NumberOfUnits | null;
}

export interface RunFeesRequest {
  guid?: string;
  scenarioGUID: string;
  smartGfeId?: string;
  appraisalWaiver?: boolean;
  useSameProviderForTitleAndSettlement?: boolean;
  providerSelectionMethod?: string;
  lookupPublicData?: boolean;
  yearBuilt?: number;
  homeSize?: number;
  lotSize?: number;
  floors?: number;
  foundationType?: number;
  newConstruction?: boolean;
  appraisalProducts?: string[];
  amcProvider?: string;
  propertyStillUnderConstruction?: boolean;
  jumboLoan?: boolean;
  rushAppraisal?: boolean;
  complexPropertyAdjustment?: boolean;
  finalOrReinspection1004D?: boolean;
  secondAppraisal?: boolean;
  outOfOfficeClosing?: boolean;
  powerOfAttorney?: boolean;
  addingOrRemovingIndividualsFromTitle?: boolean;
  titleUpdateFees?: number;
  docSigningFees?: number;
  releaseDocs?: number;
  accommodationPayoff?: number;
  resubordinations?: number;
  titleInsuranceEnforcementFundFees?: number;
  upperCourtSearches?: number;
  guarantyFees?: number;
}

export interface RunFeesResponse {
  feeEstimate?: RunFeesRequest;
}

export interface RequiredServiceMessage {
  ruleCode?: RequiredServiceRuleCode;
  rulePurpose?: string;
  message?: string;
  result?: boolean;
  status?: string;
  exceptionMessage?: string;
}

export enum RequiredServiceRuleCode {
  Appraisal = 'APPRAISAL',
  Title = 'TITLE',
  Settlement = 'SETTLEMENT',
  LandSurvey = 'LAND_SURVEY',
  PestInspection = 'PEST_INSPECTION',
  StructuralInspection = 'STRUCTURAL_INSPECTION',
}

export interface AppraisalProductResponse {
  appraisalProducts: AppraisalProduct[];
}

export interface AppraisalProduct {
  appraisalProductId: string;
  appraisalProductName: string;
}
